import { Link, useLocation } from "react-router-dom";
import { Flame, Home, Users, User, LogOut, Shield, ChevronDown, Menu } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { usePresence } from "@/hooks/usePresence";
import { Badge } from "@/components/ui/badge";
import { Button } from "./ui/button";
import lionLogo from "@/assets/lion-logo-optimized.webp";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

// Helper function to get initial logo URL from localStorage cache
const getInitialLogoUrl = () => {
  try {
    const storedData = JSON.parse(localStorage.getItem('heroLogoCache') || '{}');
    
    if (storedData?.url && storedData?.timestamp) {
      return `${storedData.url}?t=${storedData.timestamp}`;
    }
    
    return lionLogo;
  } catch {
    return lionLogo;
  }
};

const Navigation = () => {
  const location = useLocation();
  const { user, isAdmin, signOut } = useAuth();
  const { onlineUsers } = usePresence();
  const [logoUrl, setLogoUrl] = useState<string>(getInitialLogoUrl());
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const mainNavItems = [
    { path: "/", icon: Home, label: "Ana sayfa" },
    { path: "/community", icon: Users, label: "Topluluk" },
    { path: "/prognosen", icon: Flame, label: "Tahminler" },
    { path: "/lineup-builder", icon: Users, label: "11'ini kur" },
    { path: "/abstimmungen", icon: Flame, label: "Oylamalar" },
  ];

  const dropdownItems = [
    { path: "/statistiken", label: "İstatistikler" },
    { path: "/ergebnisse", label: "Fikstür" },
    { path: "/spieler", label: "Kadro" },
    { path: "/puan-durumu", label: "Puan Durumu" },
  ];

  useEffect(() => {
    const fetchLogo = async () => {
      const { data } = await supabase
        .from('hero_images')
        .select('image_url, updated_at')
        .eq('image_type', 'logo')
        .maybeSingle();
      
      if (data?.image_url) {
        try {
          const storedData = JSON.parse(localStorage.getItem('heroLogoCache') || '{}');
          
          // Create signature from URL + updated_at to detect any changes
          const signature = `${data.image_url}|${data.updated_at ?? ''}`;
          
          let timestamp: number;
          
          // Signature has changed (new URL or updated_at) or doesn't exist -> new timestamp
          if (!storedData || storedData.signature !== signature) {
            timestamp = Date.now();
            localStorage.setItem('heroLogoCache', JSON.stringify({
              url: data.image_url,
              signature: signature,
              timestamp: timestamp
            }));
          } else {
            // Signature is the same -> keep old timestamp (use browser cache)
            timestamp = storedData.timestamp;
          }
          
          const finalUrl = `${data.image_url}?t=${timestamp}`;
          setLogoUrl(finalUrl);
          
          // Optional: Preload logo for smooth transition
          const preloadImg = new Image();
          preloadImg.src = finalUrl;
        } catch (error) {
          // Fallback if localStorage fails
          setLogoUrl(`${data.image_url}?t=${Date.now()}`);
        }
      }
    };
    
    fetchLogo();
  }, []);

  const handleSignOut = async () => {
    await signOut();
  };

  const hasSidebar = [
    "/community",
    "/messages",
    "/search",
    "/profil",
    "/profile",
    "/profile-edit",
    "/follow",
    "/followlist",
  ].some((p) => location.pathname.startsWith(p));

  return (
    <nav className="sticky top-0 z-50 bg-dark-red border-b-4 border-golden-yellow shadow-2xl">
      <div className={`${hasSidebar ? "w-full" : "container mx-auto px-4"}`}>
        <div className={`flex items-center justify-between h-16 ${hasSidebar ? "px-0" : ""}`}>
          {location.pathname === "/" && (
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img 
                key={logoUrl} 
                src={logoUrl} 
                alt="Galatasaray Logo" 
                className="h-12 w-12 transition-opacity duration-300" 
              />
            </Link>
          )}

          {/* Mobile Hamburger Menu */}
          <div className="md:hidden ml-auto">
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-golden-yellow hover:bg-golden-yellow/10">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-dark-red border-l-4 border-golden-yellow w-[280px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="text-golden-yellow text-xl font-bold">Navigation</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-2 mt-6 pb-6">
                  {/* Main Nav Items */}
                  {mainNavItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-bold ${
                          isActive
                            ? "bg-golden-yellow text-dark-red shadow-md"
                            : "text-golden-yellow hover:text-cream hover:bg-golden-yellow/10"
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                  
                  {/* Separator */}
                  <div className="h-px bg-golden-yellow/20 my-2" />
                  
                  {/* Dropdown Items */}
                  {dropdownItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-bold ${
                          isActive
                            ? "bg-golden-yellow text-dark-red shadow-md"
                            : "text-golden-yellow hover:text-cream hover:bg-golden-yellow/10"
                        }`}
                      >
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                  
                  {/* Separator */}
                  <div className="h-px bg-golden-yellow/20 my-2" />
                  
                  {/* Online Users */}
                  {user && (
                    <div className="flex items-center gap-2 px-4 py-3">
                      <div className="w-2 h-2 bg-golden-yellow rounded-full animate-pulse"></div>
                      <Badge variant="secondary" className="font-mono bg-golden-yellow/20 text-golden-yellow border-golden-yellow/30">
                        {onlineUsers} online
                      </Badge>
                    </div>
                  )}
                  
                  {/* Admin Link */}
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setIsMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-bold ${
                        location.pathname === "/admin"
                          ? "bg-golden-yellow text-dark-red shadow-md"
                          : "text-golden-yellow hover:text-cream hover:bg-golden-yellow/10"
                      }`}
                    >
                      <Shield className="w-5 h-5" />
                      <span>Admin</span>
                    </Link>
                  )}
                  
                  {/* Auth Button */}
                  {user ? (
                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-bold text-golden-yellow hover:text-cream hover:bg-golden-yellow/10"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Logout</span>
                    </button>
                  ) : (
                    <Link
                      to="/auth"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-bold text-golden-yellow hover:text-cream hover:bg-golden-yellow/10"
                    >
                      <User className="w-5 h-5" />
                      <span>Giriş yap</span>
                    </Link>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Desktop Navigation - unchanged */}
          <div className="hidden md:flex gap-1 items-center">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all font-bold ${
                    isActive
                      ? "bg-golden-yellow text-dark-red shadow-md"
                      : "text-golden-yellow hover:text-cream hover:bg-golden-yellow/10"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{item.label}</span>
                </Link>
              );
            })}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-1 text-golden-yellow hover:text-cream hover:bg-golden-yellow/10 font-bold">
                  <span>Daha Fazla</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-dark-red border-2 border-golden-yellow">
                {dropdownItems.map((item) => (
                  <DropdownMenuItem key={item.path} asChild>
                    <Link to={item.path} className="w-full cursor-pointer text-golden-yellow hover:text-cream hover:bg-golden-yellow/10">
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <div className="ml-2 pl-2 border-l-2 border-golden-yellow/30 flex items-center gap-1">
              {user && (
                <div className="flex items-center gap-2 px-3 py-2 mr-2">
                  <div className="w-2 h-2 bg-golden-yellow rounded-full animate-pulse"></div>
                  <Badge variant="secondary" className="font-mono bg-golden-yellow/20 text-golden-yellow border-golden-yellow/30">
                    {onlineUsers} online
                  </Badge>
                </div>
              )}
              
              {isAdmin && (
                <Link
                  to="/admin"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all font-bold ${
                    location.pathname === "/admin"
                      ? "bg-golden-yellow text-dark-red shadow-md"
                      : "text-golden-yellow hover:text-cream hover:bg-golden-yellow/10"
                  }`}
                >
                  <Shield className="w-4 h-4" />
                  <span className="hidden sm:inline">Admin</span>
                </Link>
              )}
              
              {user ? (
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all font-bold text-golden-yellow hover:text-cream hover:bg-golden-yellow/10"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Logout</span>
                </button>
              ) : (
                <Link
                  to="/auth"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all font-bold text-golden-yellow hover:text-cream hover:bg-golden-yellow/10"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Giriş yap</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
